import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { useT } from "@/context/I18nContext";
import PageHeader from "@/components/common/PageHeader";
import EmptyState, { LoadingCard, ErrorState } from "@/components/common/EmptyState";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Users2, Bell, FolderOpen, MailPlus, FileText, Archive, Search } from "lucide-react";
import { Link } from "react-router-dom";

export function CareClients() {
  const t = useT();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [email, setEmail] = useState("");
  const list = useQuery({
    queryKey: ["care-clients"],
    queryFn: async () => (await api.get("/care/clients")).data,
    retry: false,
  });
  const items = useMemo(() => {
    const raw = Array.isArray(list.data) ? list.data : list.data?.items || list.data?.clients || [];
    if (!q) return raw;
    const s = q.toLowerCase();
    return raw.filter((c) => (c.name || c.email || "").toLowerCase().includes(s));
  }, [list.data, q]);

  const invite = async () => {
    if (!email.trim()) return;
    try {
      await api.post("/care/clients/invite", { email: email.trim() });
      setEmail("");
      toast.success(t("common.saved"));
      qc.invalidateQueries({ queryKey: ["care-clients"] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const archive = async (id) => {
    try {
      await api.post(`/care/clients/${id}/archive`);
      toast.success(t("common.saved"));
      qc.invalidateQueries({ queryKey: ["care-clients"] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow={t("nav.care")} title={t("care.clients")} subtitle={t("care.subtitle")} />
      <div className="rounded-3xl bg-white shadow-card p-4 md:p-6 flex flex-wrap gap-2">
        <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="client@example.com" className="rounded-xl bg-cream-50 h-11 max-w-xs" data-testid="care-invite-email" />
        <Button onClick={invite} className="rounded-full bg-accents-care hover:opacity-90 h-11 px-5 text-white" data-testid="care-invite-submit">
          <MailPlus className="h-4 w-4 mr-1.5" /> {t("care.inviteClient")}
        </Button>
        <div className="flex-1" />
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-ink-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("common.search")} className="rounded-xl bg-cream-50 h-11 max-w-xs" data-testid="care-clients-search" />
        </div>
      </div>

      {list.isLoading && <LoadingCard />}
      {list.isError && <ErrorState message={errMsg(list.error)} onRetry={() => list.refetch()} />}
      {!list.isLoading && !list.isError && items.length === 0 && (
        <EmptyState icon={Users2} title={t("common.empty")} testId="care-clients-empty" />
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="care-clients-list">
        {items.map((c, i) => {
          const id = c.link_id || c.id || c._id;
          return (
            <div key={id || i} className="rounded-2xl bg-white shadow-card p-4">
              <div className="text-ink-900 font-medium truncate">{c.name || "—"}</div>
              <div className="text-xs text-ink-500 truncate">{c.email || ""}</div>
              {c.status && <div className="text-xs text-sage-700 mt-1">{c.status}</div>}
              <div className="flex flex-wrap gap-1.5 mt-3">
                {id && (
                  <>
                    <Link to={`/app/care/clients/${id}`} className="rounded-full border border-border h-8 px-3 text-xs inline-flex items-center hover:bg-cream-50" data-testid={`care-client-open-${i}`}>
                      {t("common.open")}
                    </Link>
                    <button onClick={() => archive(id)} className="rounded-full border border-border h-8 px-3 text-xs inline-flex items-center hover:bg-cream-50 text-rust-600" data-testid={`care-client-archive-${i}`}>
                      <Archive className="h-3.5 w-3.5 mr-1" /> {t("care.archiveClient")}
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function CareClientDetail() {
  const t = useT();
  const [params] = React.useState(() => window.location.pathname.split("/").filter(Boolean));
  const linkId = params[params.length - 1];
  const detail = useQuery({
    queryKey: ["care-client", linkId],
    queryFn: async () => (await api.get(`/care/clients/${linkId}`)).data,
    retry: false,
  });
  const trends = useQuery({
    queryKey: ["care-client-trends", linkId],
    queryFn: async () => (await api.get(`/care/clients/${linkId}/trends`)).data,
    retry: false,
  });
  const reports = useQuery({
    queryKey: ["care-client-reports", linkId],
    queryFn: async () => (await api.get(`/care/clients/${linkId}/reports`)).data,
    retry: false,
  });

  const generate = async () => {
    try {
      await api.post(`/care/clients/${linkId}/reports/generate`);
      toast.success(t("common.saved"));
      reports.refetch();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const rlist = Array.isArray(reports.data) ? reports.data : reports.data?.items || [];

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t("care.title")}
        title={detail.data?.name || t("care.clients")}
        subtitle={detail.data?.email || ""}
        actions={
          <Button onClick={generate} data-testid="care-generate-report" className="rounded-full bg-accents-care hover:opacity-90 h-11 px-5 text-white">
            <FileText className="h-4 w-4 mr-1.5" /> {t("care.generateReport")}
          </Button>
        }
      />
      <div className="rounded-3xl bg-white shadow-card p-6" data-testid="care-client-consent">
        <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-ink-500">{t("b2b.consent.categories")}</div>
        {(() => {
          const d = detail.data || {};
          const consent = d.consent || d.categories || d.shared_categories || {};
          const entries = Array.isArray(consent) ? consent.map((k) => [k, true]) : Object.entries(consent);
          const shared = entries.filter(([, v]) => v).map(([k]) => k);
          const status = String(d.status || d.link_status || "").toLowerCase();
          return (
            <div className="mt-3">
              <div className="text-xs text-ink-500 mb-2">{t("care.status") || "Status"}: <span className="font-semibold text-ink-900">{status || "—"}</span></div>
              {shared.length ? (
                <div className="flex flex-wrap gap-2">{shared.map((k) => <span key={k} className="rounded-full bg-sage-100 text-sage-700 px-3 py-1 text-xs font-semibold" data-testid={`care-shared-${k}`}>{t(`b2b.consent.cat.${k}`)}</span>)}</div>
              ) : (
                <div className="text-sm text-ink-700">{t("b2b.oh.nothingShared")}</div>
              )}
            </div>
          );
        })()}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-3xl bg-white shadow-card p-6" data-testid="care-client-trends">
          <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-ink-500">{t("care.trends")}</div>
          <pre className="text-xs text-ink-700 mt-3 whitespace-pre-wrap font-mono max-h-72 overflow-auto">
            {trends.isLoading ? t("common.loading") : JSON.stringify(trends.data, null, 2) || t("common.empty")}
          </pre>
        </div>
        <div className="rounded-3xl bg-white shadow-card p-6" data-testid="care-client-reports">
          <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-ink-500">{t("care.weeklyReport")}</div>
          {reports.isLoading && <LoadingCard height="h-24" />}
          {rlist.length === 0 && !reports.isLoading && <div className="text-sm text-ink-500 mt-3">{t("common.empty")}</div>}
          <div className="mt-3 space-y-2">
            {rlist.map((r, i) => (
              <div key={r.id || i} className="rounded-2xl border border-border p-3">
                <div className="text-xs text-ink-500">{r.created_at && new Date(r.created_at).toLocaleString()}</div>
                <div className="text-sm text-ink-900 mt-1 whitespace-pre-wrap line-clamp-6">{r.summary || r.text || r.content || "—"}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function CareAlerts() {
  const t = useT();
  const q = useQuery({
    queryKey: ["care-alerts"],
    queryFn: async () => (await api.get("/care/alerts")).data,
    retry: false,
  });
  const items = Array.isArray(q.data) ? q.data : q.data?.items || q.data?.alerts || [];
  return (
    <div className="space-y-6">
      <PageHeader eyebrow={t("nav.care")} title={t("care.alerts")} subtitle={t("care.subtitle")} />
      {q.isLoading && <LoadingCard />}
      {q.isError && <ErrorState message={errMsg(q.error)} onRetry={() => q.refetch()} />}
      {!q.isLoading && !q.isError && items.length === 0 && (
        <EmptyState icon={Bell} title={t("care.noAlerts")} testId="care-alerts-empty" />
      )}
      <div className="space-y-2" data-testid="care-alerts">
        {items.map((a, i) => (
          <div key={a.id || i} className="rounded-2xl bg-white shadow-card p-4 border-l-4 border-rust-500">
            <div className="text-xs text-ink-500">{a.created_at && new Date(a.created_at).toLocaleString()}</div>
            <div className="font-heading text-ink-900 mt-1">{a.client_name || a.name || "Client"}</div>
            <div className="text-sm text-ink-700 mt-1">{a.reason || a.message || a.text || ""}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function CareDossier() {
  const t = useT();
  const q = useQuery({
    queryKey: ["care-dossier"],
    queryFn: async () => (await api.get("/care/dossier")).data,
    retry: false,
  });
  const items = Array.isArray(q.data) ? q.data : q.data?.items || q.data?.entries || [];
  return (
    <div className="space-y-6">
      <PageHeader eyebrow={t("nav.care")} title={t("care.dossier")} subtitle={t("care.subtitle")} />
      {q.isLoading && <LoadingCard />}
      {q.isError && <ErrorState message={errMsg(q.error)} onRetry={() => q.refetch()} />}
      {!q.isLoading && !q.isError && items.length === 0 && (
        <EmptyState icon={FolderOpen} title={t("common.empty")} testId="care-dossier-empty" />
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="care-dossier-list">
        {items.map((d, i) => (
          <div key={d.id || i} className="rounded-2xl bg-white shadow-card p-4">
            <div className="text-ink-900 font-medium truncate">{d.title || d.client_name || d.name || "Entry"}</div>
            <div className="text-xs text-ink-500 mt-1">{d.created_at && new Date(d.created_at).toLocaleString()}</div>
            <div className="text-sm text-ink-700 mt-2 line-clamp-4">{d.summary || d.text || d.notes || ""}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
